<template>
    <div
        class="otr-row"
        :class="{ selected }"
        :style="{ left: leftPct + '%', width: widthPct + '%', backgroundColor: color }"
        :title="label"
        @mousedown="onBodyMouseDown"
    >
        <div class="otr-handle otr-handle-left" @mousedown.stop="e => onEdgeMouseDown('start', e)"></div>
        <span class="otr-label">{{ label }}</span>
        <i class="fa-solid fa-clone otr-duplicate" @mousedown.stop @click.stop="$emit('duplicate')"></i>
        <i class="fa-solid fa-trash otr-remove" @mousedown.stop @click.stop="$emit('remove')"></i>
        <div class="otr-handle otr-handle-right" @mousedown.stop="e => onEdgeMouseDown('end', e)"></div>
    </div>
</template>

<script>
import { WIDGET_ACCENT_COLORS } from '../engine/constants.js'
import { getWidgetDisplayText } from '../engine/widgets/widgetRenderers.js'

const MIN_DURATION_MS = 200

export default {
    name: 'OverlayTrackRow',

    props: {
        overlay: {
            type: Object,
            required: true
        },
        totalDurationMs: {
            type: Number,
            required: true
        },
        selected: {
            type: Boolean,
            default: false
        }
    },

    computed: {
        leftPct() {
            return (this.overlay.startMs / this.totalDurationMs) * 100
        },
        widthPct() {
            return Math.max(2, ((this.overlay.endMs - this.overlay.startMs) / this.totalDurationMs) * 100)
        },
        color() {
            if (this.overlay.type === 'widget') return WIDGET_ACCENT_COLORS[this.overlay.widgetType] || '#5c5c5c'
            if (this.overlay.type === 'shape') return '#8a8a8a'
            return '#2f1937'
        },

        // "Type : contenu" so a narrow, unlabeled colored bar in a busy timeline can actually be
        // told apart (a mention isn't a hashtag isn't a piece of text at a glance otherwise).
        label() {
            const data = this.overlay.data || {}
            if (this.overlay.type === 'widget') return getWidgetDisplayText(this.overlay) || this.$t('dash.post-creator.media-editor.track-row.fallback-widget')
            if (this.overlay.type === 'shape') return `${this.$t('dash.post-creator.media-editor.track-row.type-shape')} : ${data.shape || ''}`
            if (this.overlay.type === 'text') return `${this.$t('dash.post-creator.media-editor.track-row.type-text')} : ${(data.text || '').slice(0, 24)}`
            return ''
        }
    },

    methods: {
        onBodyMouseDown(e) {
            this.$emit('select')
            const ruler = e.currentTarget.closest('.ot-tracks-ruler')
            if (!ruler) return

            const rulerWidth = ruler.getBoundingClientRect().width
            const msPerPx = this.totalDurationMs / rulerWidth
            const startX = e.clientX
            const originalStart = this.overlay.startMs
            const duration = this.overlay.endMs - this.overlay.startMs

            // Isolated onto its own row for the whole drag (see Timeline.vue's overlayRows) so
            // repacking every other element around its live position can't make it jump to a
            // different lane mid-drag - that jumpiness was what made dragging feel broken/imprecise.
            this.$emit('drag-start')

            const onMove = (moveEvent) => {
                const deltaMs = (moveEvent.clientX - startX) * msPerPx
                const newStart = Math.min(Math.max(0, originalStart + deltaMs), this.totalDurationMs - duration)
                this.$emit('update', { startMs: Math.round(newStart), endMs: Math.round(newStart + duration) })
            }
            const onUp = () => {
                document.removeEventListener('mousemove', onMove)
                document.removeEventListener('mouseup', onUp)
                this.$emit('drag-end')
            }
            document.addEventListener('mousemove', onMove)
            document.addEventListener('mouseup', onUp)
        },

        onEdgeMouseDown(edge, e) {
            this.$emit('select')
            const ruler = e.target.closest('.ot-tracks-ruler')
            if (!ruler) return

            const rulerWidth = ruler.getBoundingClientRect().width
            const msPerPx = this.totalDurationMs / rulerWidth
            const startX = e.clientX
            const originalStart = this.overlay.startMs
            const originalEnd = this.overlay.endMs

            this.$emit('drag-start')

            const onMove = (moveEvent) => {
                const deltaMs = (moveEvent.clientX - startX) * msPerPx
                if (edge === 'start') {
                    const newStart = Math.min(Math.max(0, originalStart + deltaMs), originalEnd - MIN_DURATION_MS)
                    this.$emit('update', { startMs: Math.round(newStart), endMs: originalEnd })
                } else {
                    const newEnd = Math.max(Math.min(this.totalDurationMs, originalEnd + deltaMs), originalStart + MIN_DURATION_MS)
                    this.$emit('update', { startMs: originalStart, endMs: Math.round(newEnd) })
                }
            }
            const onUp = () => {
                document.removeEventListener('mousemove', onMove)
                document.removeEventListener('mouseup', onUp)
                this.$emit('drag-end')
            }
            document.addEventListener('mousemove', onMove)
            document.addEventListener('mouseup', onUp)
        }
    }
}
</script>

<style lang="scss" scoped></style>
